import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, throwError } from 'rxjs';
import { catchError, map, pluck, tap } from 'rxjs/operators';
import { APP_CONFIG } from 'src/app/core/services/config.service';
import { ProjectService } from 'src/app/core/services/project.service';
import { TooMuchLoadsComponent } from 'src/app/modal/components/too-much-loads/too-much-loads.component';
import { ModalService } from 'src/app/modal/modal.service';
import { Project, VersionsProject } from '../objects/project';
import { User } from '../objects/user';
import { Company, Loads, Notifiactions, SavedProject, ShareIdProject, Trucks } from '../types';
import { NewsNotificationsService } from './news-notifications.service';
import { PalletOverflowRecommendationService } from './pallet-overflow-recommendation.service';
import { ProjectHistoryService } from './project-history.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class UserApiService {
  private url = 'http://localhost:8080/api/';

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private translate: TranslateService,
    private projectService: ProjectService,
    private modalService: ModalService,
    private projectHistory: ProjectHistoryService,
    private router: Router,
    private route: ActivatedRoute,
    private cookieService: CookieService,
    private newsNotificationService: NewsNotificationsService,
    private palletOverflowRecommendation: PalletOverflowRecommendationService,
  ) {
    APP_CONFIG()
      .pipe(pluck('baseUri'))
      .subscribe((url: string) => (this.url = url));
  }

  public getUser(): Observable<any> {
    return this.http.get(this.url + 'user').pipe(
      tap((userData: User) => this.handleSuccess(userData)),
      catchError((error: HttpErrorResponse) => this.instantHandleError(error)),
    );
  }

  public saveProject(projectData): Observable<any> {
    return this.http.put(this.url + 'user/project', projectData).pipe(
      tap((projects: any[]) => this.handleSaveProjectSuccess(projects)),
      catchError((error: HttpErrorResponse) => this.instantHandleError(error)),
    );
  }

  public updateProject(updateProjectData) {
    return this.http.post(this.url + 'user/project', updateProjectData).pipe(
      tap((projects: any[]) => this.handleSaveProjectSuccess(projects)),
      catchError((error: HttpErrorResponse) => this.instantHandleError(error)),
    );
  }

  public transferOldUserData(): Observable<any> {
    return this.http.get(this.url + 'user/data/transfer').pipe(
      tap((user) => this.handleTransferSucces(user)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public updateUserInfo(userData) {
    return this.http.post(this.url + 'user/data/update', { data: userData }).pipe(
      tap((loadsData: User) => this.handleUserInfoUpdateSuccess(loadsData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public removeAccountRequest() {
    return this.http
      .post(this.url + 'user/remove/email', {
        data: { lang: this.translate.currentLang },
      })
      .pipe(
        tap(() => this.handleRemoveAccountRequestSuccess()),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public saveCompany(company: Company, options?: { skipVatValidation?: boolean }) {
    const skipVatValidation =
      company.country === 'PL' ? false : Boolean(options?.skipVatValidation);
    return this.http
      .post(this.url + 'user/company/save', { ...company, skipVatValidation })
      .pipe(
      tap((companyData: Company) => this.handleCompanyActionSuccess(companyData)),
      catchError((error: HttpErrorResponse) => this.instantHandleError(error)),
    );
  }

  public updateCompany(
    company: Company,
    options?: { skipVatValidation?: boolean },
  ) {
    const skipVatValidation =
      company.country === 'PL' ? false : Boolean(options?.skipVatValidation);
    return this.http
      .post(this.url + 'user/company/update', { ...company, skipVatValidation })
      .pipe(
      tap((companyData: Company) => this.handleCompanyActionSuccess(companyData)),
      catchError((error: HttpErrorResponse) => this.instantHandleError(error)),
    );
  }

  public findCompany(
    vatId: string,
    country: string,
  ): Observable<{ valid: boolean; name?: string; address?: string }> {
    return this.http
      .post<{ valid: boolean; name?: string; address?: string }>(
        this.url + 'user/company/find',
        { vatId, country },
      )
      .pipe(catchError((error: HttpErrorResponse) => this.instantHandleError(error)));
  }

  public saveLoad(loadData) {
    return this.http.post(this.url + 'user/load/save', { data: loadData }).pipe(
      tap((loadsData: Loads) => this.handleLoadActionSuccess(loadsData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public saveLoads(loadData) {
    return this.http.post(this.url + 'user/load/save/bulk', { data: loadData }).pipe(
      tap((loadsData: Loads) => this.handleLoadActionSuccess(loadsData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public updateLoad(loadData) {
    return this.http.post(this.url + 'user/load/update', { data: loadData }).pipe(
      tap((loadsData: Loads) => this.handleLoadActionSuccess(loadsData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public deleteLoad(loadId) {
    return this.http.post(this.url + 'user/load/delete', { data: loadId }).pipe(
      tap((loadsData: Loads) => this.handleLoadActionSuccess(loadsData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public saveLoadingSpace(loadData) {
    return this.http.post(this.url + 'user/truck/save', { data: loadData }).pipe(
      tap((loadingSpacesData: Trucks) => this.handleLoadingSpaceActionSuccess(loadingSpacesData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public saveLoadingSpaces(loadData) {
    return this.http.post(this.url + 'user/truck/save/bulk', { data: loadData }).pipe(
      tap((loadingSpacesData: Trucks) => this.handleLoadingSpaceActionSuccess(loadingSpacesData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public updateLoadingSpace(loadData) {
    return this.http.post(this.url + 'user/truck/update', { data: loadData }).pipe(
      tap((loadingSpacesData: Trucks) => this.handleLoadingSpaceActionSuccess(loadingSpacesData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public deleteLoadingSpace(loadId) {
    return this.http.post(this.url + 'user/truck/delete', { data: loadId }).pipe(
      tap((loadingSpacesData: Trucks) => this.handleLoadingSpaceActionSuccess(loadingSpacesData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public updateSavedProject(savedProjectData) {
    return this.http.post(this.url + 'user/project', savedProjectData).pipe(
      tap((savedProjectsData: SavedProject[]) =>
        this.handleSavedProjectActionSuccess(savedProjectsData),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public updateCompanyProject(savedProjectData): Observable<any> {
    return this.http.post(this.url + 'user/project/company', savedProjectData).pipe(
      tap((savedProjectsData: SavedProject[]) =>
        this.handleSavedProjectActionSuccess(savedProjectsData),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public deleteSavedProject(savedProjectId: string) {
    return this.http.delete(this.url + `user/project?id=${savedProjectId}`).pipe(
      tap((savedProjectsData: SavedProject[]) =>
        this.handleSavedProjectActionSuccess(savedProjectsData),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public shareSavedProject(savedProject) {
    return this.http.post(this.url + 'user/project/share', savedProject).pipe(
      map((shareData: ShareIdProject) => this.handleSavedProjectShareSuccess(shareData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public shareCompanyProject(savedProject) {
    return this.http.post(this.url + 'user/project/company/share', savedProject).pipe(
      map((shareData: ShareIdProject) => this.handleSavedProjectShareSuccess(shareData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public unshareCompanyProject(data: any) {
    return this.http.post(this.url + 'user/project/unshareCompany', data).pipe(
      map(() => this.handleUnshareCompanyProjectSuccess()),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public shareProjectForCompany(data: any) {
    return this.http.post(this.url + 'user/project/shareCompany', data).pipe(
      map(() => this.handleUnshareCompanyProjectSuccess()),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public getCompanyProjectById(id: number, version: number): Observable<any> {
    const versionString = version !== null && version !== undefined ? `&version=${version}` : '';
    return this.http
      .get(this.url + `user/project/company/data?id=${id}${versionString}`)
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  public getCompanyProject(
    companyProjectId: number | string,
    version: number = null,
  ): Observable<any> {
    this.projectService.setCurrentWorkSpace(0);
    this.projectService.setClosedLoadingSpaces([]);
     
    return this.getCompanyProjectById(companyProjectId as unknown as number, version).pipe(
      tap((projectData: VersionsProject) => {
        const projectData2 = projectData.project;
        this.projectService.setCurrentNumberOfLoads(projectData2);
        const quantity = this.projectService.getProjectQuantity();
        if (quantity > 10000) {
          return this.modalService
            .open(TooMuchLoadsComponent, { message: 'too_much_loads_project' })
            .subscribe((result: boolean) => {
              if (result) {
                this.handleOpenSavedProjectSuccess(projectData2);
              } else {
                this.http
                  .get(this.url + 'project/new')
                  .pipe(
                    tap((project: Project) => {
                      this.palletOverflowRecommendation.onProjectReplaced();
                      this.projectService.setState(project);
                      this.projectHistory.updateHistory();
                    }),
                    catchError((error: HttpErrorResponse) => this.handleError(error)),
                  )
                  .subscribe();
              }
            });
        } else {
          this.handleOpenSavedProjectSuccess(projectData2);
        }
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public openSavedProject(
    savedProjectId: number | string,
    version: number = null,
  ): Observable<Project> {
    this.projectService.setCurrentWorkSpace(0);
    this.projectService.setClosedLoadingSpaces([]);
    const openSavedProjectUrl: string =
      version !== null && version !== 0
        ? `user/project/open?id=${savedProjectId}&version=${version}`
        : `user/project/open?id=${savedProjectId}`;
    return this.http.get(this.url + openSavedProjectUrl).pipe(
      tap((projectData: Project) => {
        this.projectService.setCurrentNumberOfLoads(projectData);
        const quantity = this.projectService.getProjectQuantity();
        if (quantity > 10000) {
          return this.modalService
            .open(TooMuchLoadsComponent, { message: 'too_much_loads_project' })
            .subscribe((result: boolean) => {
              if (result) {
                this.handleOpenSavedProjectSuccess(projectData);
              } else {
                this.http
                  .get(this.url + 'project/new')
                  .pipe(
                    tap((project: Project) => {
                      this.palletOverflowRecommendation.onProjectReplaced();
                      this.projectService.setState(project);
                      this.projectHistory.updateHistory();
                    }),
                    catchError((error: HttpErrorResponse) => this.handleError(error)),
                  )
                  .subscribe();
              }
            });
        } else {
          this.handleOpenSavedProjectSuccess(projectData);
        }
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public validateDeleteAccountCode(code) {
    return this.http.post(this.url + 'user/remove/code/validate', { data: { code: code } }).pipe(
      tap(),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public cancelDeleteAccountCode(code) {
    return this.http.post(this.url + 'user/remove/cancel', { data: { code: code } }).pipe(
      tap(),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public deleteAccount(code) {
    return this.http.post(this.url + 'user/remove', { data: { code: code } }).pipe(
      tap(),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public disqualifyTrial() {
    return this.http.post(this.url + 'user/trial/disqualify', localStorage.token).pipe(
      tap(),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public getNewsNotifications(lang: string): Observable<any> {
    return this.http.get(this.url + `user/news-notifications/${lang}`).pipe(
      tap((data) => this.handleSuccessNewsNotifications(data)),
      catchError((error: HttpErrorResponse) => this.instantHandleError(error)),
    );
  }

  public markSeenNewsNotifications(lang: string, seenNotificationsIds: number[]) {
    return this.http
      .post(this.url + `user/news-notifications/${lang}/mark-seen`, { seenNotificationsIds })
      .pipe(
        tap((data) => this.handleSuccessNewsNotifications(data)),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public dismissNewsNotifications(lang: string, newsNotificationId: number) {
    return this.http
      .post(this.url + `user/news-notifications/${lang}/dismiss`, { newsNotificationId })
      .pipe(
        tap((data) => this.handleSuccessNewsNotifications(data)),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public updateUserNotification(
    notificationId: number,
  ): Observable<{ notifications: Notifiactions[] }> {
    return this.http
      .post<{ notifications: Notifiactions[] }>(this.url + 'user/notification/update', {
        notificationId,
      })
      .pipe(
        tap((res) => {
          const userData = this.userService.getState();
          this.userService.setState({ ...userData, notifications: res.notifications });
        }),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  private handleSuccess(user: User) {
    localStorage.token = user.token;
    if (this.cookieService.get('currentUser') !== user.email) {
      this.projectService.setClosedLoadingSpaces([]);
      this.cookieService.delete('welcome', '/', null, false, 'Lax');
      this.cookieService.delete('welcomeApi', '/', null, false, 'Lax');
      this.cookieService.delete('issueDate', '/', null, false, 'Lax');
      this.cookieService.set('currentUser', user.email, undefined, '/', null, false, 'Lax');
    }
    this.userService.setState(user);
  }

  private handleSaveProjectSuccess(projects: any[]) {
    const userData = this.userService.getState();
    userData.savedProjects = projects;
    this.userService.setState(userData);
  }

  private handleRemoveAccountRequestSuccess() {
    return;
  }

  private handleOpenSavedProjectSuccess(projectData: Project) {
    this.palletOverflowRecommendation.onProjectReplaced();
    this.projectService.setState(projectData);
    this.projectHistory.updateHistory();
  }

  private handleLoadActionSuccess(loads: Loads) {
    const userData = this.userService.getState();
    userData.loads = loads;
    this.userService.setState(userData);
  }

  private handleCompanyActionSuccess(company: Company) {
    const userData = this.userService.getState();
    userData.company = company;
    this.userService.setState(userData);
  }

  private handleLoadingSpaceActionSuccess(loadingSpaces: Trucks) {
    const userData = this.userService.getState();
    userData.trucks = loadingSpaces;
    this.userService.setState(userData);
  }

  private handleSavedProjectActionSuccess(savedProjects: SavedProject[]) {
    const userData = this.userService.getState();
    userData.savedProjects = savedProjects;
    this.userService.setState(userData);
  }

  private handleSavedProjectShareSuccess(shareData: ShareIdProject) {
    return shareData.id;
  }

  private handleUnshareCompanyProjectSuccess(): void {
    this.getUser().subscribe((userData: User) => {
      this.userService.setState(userData);
    });
  }

  private handleUserInfoUpdateSuccess(userInfo: User) {
    let userData = this.userService.getState();
    userData = Object.assign(userData, userInfo);
    if (this.cookieService.get('currentUser') !== userData.email) {
      this.projectService.setClosedLoadingSpaces([]);
      this.cookieService.delete('welcome', '/', null, false, 'Lax');
      this.cookieService.delete('welcomeApi', '/', null, false, 'Lax');
      this.cookieService.set('currentUser', userData.email, undefined, '/', null, false, 'Lax');
    }
    this.userService.setState(userData);
  }

  private handleSuccessNewsNotifications(response) {
    this.newsNotificationService.setState(response);
  }

  private handleTransferSucces(user): void {
    if (user) {
      this.userService.setState(user);
    }
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 401) {
      this.router.navigate([this.translate.currentLang, 'auth'], {
        relativeTo: this.route,
      });
    } else if (error.status === 403) {
      window.location.reload();
    }
    return throwError(error);
  }

  private instantHandleError(error: HttpErrorResponse) {
    return throwError(error);
  }
}
